import { isProduction } from '@glow/common'
import { dynamicImportErrors, SiteMetricsErrorBoundary } from '@glow/instrumentation'
import { ButtonBase } from '@glow/ui-components'
import { Fragment, ReactElement, ReactNode, useEffect } from 'react'
import { ErrorImage } from '../assets/ErrorImage'
import { getPageReloadedWithin10Sec, setPageReloadedWithin10Sec } from './util'

interface Props {
  title?: string
  children: ReactNode
}

function MainBox(props: Props): ReactElement {
  return (
    <section className="bg-theme-surface rounded-xs shadow-dark p-4 sm:p-8">
      {props.title && <h1 className="font-semibold text-base mb-4 sm:mb-8">{props.title}</h1>}
      {props.children}
    </section>
  )
}

export function ErrorBoundary({
  isRunningTests,
  children,
  title,
  buttonText
}: {
  children: ReactNode
  isRunningTests: boolean
  buttonText: string
  title: string
}): ReactElement {
  if (isRunningTests) {
    // Let errors thrown during testing bubble all the way up
    // so that Jest shows errors in the terminal as expected.
    return <Fragment>{children}</Fragment>
  }

  return (
    <SiteMetricsErrorBoundary
      fallback={(props) => <ErrorBoundaryFallback {...props} title={title} buttonText={buttonText} />}
    >
      {children}
    </SiteMetricsErrorBoundary>
  )
}

export function ErrorBoundaryFallback(props: {
  error: unknown
  componentStack: string | null
  buttonText: string
  title: string
  resetError: () => void
}): ReactElement {
  useEffect(() => {
    if (
      props.error instanceof Error &&
      dynamicImportErrors.some((message) => props.error instanceof Error && props.error.message.includes(message)) &&
      !getPageReloadedWithin10Sec(localStorage)
    ) {
      setPageReloadedWithin10Sec(localStorage)
      window.location.reload()
    }
  }, [props.error])
  const isProd = isProduction()
  return (
    <div className="w-[90vw] mx-auto mt-4">
      <div className="flex flex-col items-center gap-4">
        <ErrorImage />
        <MainBox title={isProd ? props.title : JSON.stringify(props.error)}>
          <div className="flex flex-col gap-4">
            <ButtonBase variant="primary" onClick={() => window.location.reload()}>
              {props.buttonText}
            </ButtonBase>
          </div>
        </MainBox>
      </div>

      {!isProd && props.componentStack && (
        <div className="flex flex-col items-center mt-4">
          <pre
            className="p-4
  w-[90vw]
  sm:p-8
  overflow-auto
  rounded
  text-xs
  border
  border-gray-300
  text-emphasis-medium"
          >
            {props.componentStack.trim()}
          </pre>
        </div>
      )}
    </div>
  )
}
